/**
 * @file React hook — useBackgroundData (state and side effects).
 * Background Data Hook
 * Main hook for managing background data form state and logic
 */

import { useEffect, useMemo, useState } from "react";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import { FORM_STAGE_CODES } from "../../../../../../constants/formStages";
import {
  FormDataResponse,
  FormField,
} from "../../../../../../interfaces/formInterfaces";
import { toast } from "react-toastify";
import { saveBackgroundData, uploadBackgroundFiles } from "../../../../../../services/submissionService";
import { getBackgroundStageDisplayFields, getBackgroundDataTypeField } from "../../../../../../components/submission/backgroundStageFieldOrder";
import {
  dataTypeFieldOptions,
  normalizeDataTypeValue,
} from "../../../../../../components/submission/backgroundDataType";
import { useBackgroundPrefill } from "./useBackgroundPrefill";
import { buildBackgroundPayload } from "../utils/backgroundPayloadBuilder";
import { resolveTimelineAvailableDefault } from "../utils/timelineDefaults";
import { resolveA1A3CheckboxDefault } from "../utils/dataCompletenessDefaults";
import {
  getFirstBackgroundFormError,
  validateBackgroundForm,
} from "../utils/validateBackgroundForm";
import {
  buildDataReviewerGroups,
  dataReviewerEmailKey,
  dataReviewerNameKey,
  normalizeDataReviewValue,
  requiredDataReviewerCount,
} from "../utils/backgroundDataReviewers";
import { useStateDropdown } from "../../../../../../hooks/useStateDropdown";
import { useGeographicScopeDropdown } from "../../../../../../hooks/useGeographicScopeDropdown";
import {
  BACKGROUND_GEO_SELECTION_FIELD,
  BACKGROUND_GEO_SPEC_FIELD,
  type BackgroundGeoSpec,
} from "../../../../../../utils/backgroundGeographicalSpecificity";

/** Type definition — Material. */
export interface Material {
  id: string;
  name: string;
  code: string;
  subMaterials: { id: string; name: string }[];
}

/** Type definition — Unit Option. */
export interface UnitOption {
  id: number;
  propertyId: number;
  unitSymbol: string;
  displayName?: string;
  isBase?: boolean;
}

/** Type definition — Specificity Option. */
export interface SpecificityOption {
  id: string;
  name: string;
}

function normalizeSpecificityName(value: unknown): string {
  if (typeof value === "string") return value;
  const v = value as Record<string, unknown> | null | undefined;
  return String(
    v?.name ??
      v?.label ??
      v?.title ??
      v?.manufacturerName ??
      v?.technologyName ??
      "Unnamed"
  );
}

function mapTechnologyRow(item: unknown): SpecificityOption | null {
  const row = item as Record<string, unknown>;
  const id = Number(row?.id ?? row?.technologyId);
  if (Number.isNaN(id)) return null;
  return { id: String(id), name: normalizeSpecificityName(item) };
}

function mapManufacturerRow(item: unknown): SpecificityOption | null {
  const row = item as Record<string, unknown>;
  const id = Number(row?.id ?? row?.manufacturerId);
  if (Number.isNaN(id)) return null;
  return { id: String(id), name: normalizeSpecificityName(item) };
}

function extractListPayload(res: { data?: unknown }): unknown[] {
  const d = res.data as Record<string, unknown> | unknown[] | undefined;
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object" && Array.isArray((d as { data?: unknown[] }).data)) {
    return (d as { data: unknown[] }).data;
  }
  return [];
}

/** Hook: use Background Data. */
export function useBackgroundData(submissionId?: string) {
  const { stateOptions } = useStateDropdown();
  const { geographicScopeOptions, loading: geographicScopeLoading } =
    useGeographicScopeDropdown();
  const [formData, setFormData] = useState<FormDataResponse | null>(null);
  const [values, setValues] = useState<Record<string, string | File>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [materials, setMaterials] = useState<Material[]>([]);
  const [subMaterials, setSubMaterials] = useState<
    { id: string; name: string }[]
  >([]);
  const [rawMaterials, setRawMaterials] = useState<
    { id: string; name: string }[]
  >([]);
  const [technologyOptions, setTechnologyOptions] = useState<SpecificityOption[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<SpecificityOption[]>([]);
  const [allUnits, setAllUnits] = useState<UnitOption[]>([]);
  const [enabledPairs, setEnabledPairs] = useState<Record<string, boolean>>({
    density: false,
    uValue: false,
    compressiveStrength: false,
    tensileStrength: false,
    temperature: false,
    diameter: false,
    coverage: false,
    thickness: false,
  });

  // Manage dynamic raw material groups (1-based indexes)
  const [rawMaterialGroups, setRawMaterialGroups] = useState<number[]>([1]);

  const dataReviewFieldOptions = useMemo(() => {
    if (!formData) return [];
    const dataReviewField = getBackgroundStageDisplayFields(formData).find(
      (f) => f.name === "dataReview"
    );
    return (
      dataReviewField?.dropdown?.options ??
      dataReviewField?.dropdownValues ??
      []
    );
  }, [formData]);

  const dataTypeFieldOptionsList = useMemo(
    () => dataTypeFieldOptions(getBackgroundDataTypeField(formData)),
    [formData]
  );

  const normalizedDataReview = useMemo(
    () => normalizeDataReviewValue(values.dataReview, dataReviewFieldOptions),
    [values.dataReview, dataReviewFieldOptions]
  );

  const reviewerGroups = useMemo(
    () =>
      buildDataReviewerGroups(
        requiredDataReviewerCount(normalizedDataReview, dataReviewFieldOptions)
      ),
    [normalizedDataReview, dataReviewFieldOptions]
  );

  const externalDataReviewSelected = reviewerGroups.length > 0;

  const { prefillFromSubmission } = useBackgroundPrefill();

  // Coerce saved label text → internal key (one_external_reviewer, etc.)
  useEffect(() => {
    if (!formData) return;
    const raw = String(values.dataReview ?? "").trim();
    if (!raw) return;
    const slug = normalizeDataReviewValue(raw, dataReviewFieldOptions);
    if (slug && slug !== raw) {
      setValues((prev) => ({ ...prev, dataReview: slug }));
    }
  }, [formData, dataReviewFieldOptions, values.dataReview, setValues]);

  useEffect(() => {
    if (!formData) return;
    const raw = String(values.dataType ?? "").trim();
    if (!raw) return;
    const slug = normalizeDataTypeValue(raw, dataTypeFieldOptionsList);
    if (slug && slug !== raw) {
      setValues((prev) => ({ ...prev, dataType: slug }));
    }
  }, [formData, dataTypeFieldOptionsList, values.dataType, setValues]);

  // Paired Field Definitions
  const pairedGroups: Record<string, string> = {
    density: "density_unit",
    uValue: "uValue_unit",
    compressiveStrength: "compressiveStrength_unit",
    tensileStrength: "tensileStrength_unit",
    temperature: "temperature_unit",
    diameter: "diameterUnit",
    coverage: "coverageUnit",
    thickness: "thicknessUnit",
  };

  const isYes = (v: unknown) => {
    const s = String(v ?? "").trim().toLowerCase();
    return s === "yes" || s === "y" || s === "true" || s === "1";
  };

  // Add raw material group (max 10)
  const addRawMaterialGroup = () => {
    setRawMaterialGroups((prev) => {
      if (prev.length >= 10) {
        toast.warning("Maximum 10 raw materials allowed");
        return prev;
      }
      return [...prev, prev.length + 1];
    });
  };

  // Remove raw material group
  const removeRawMaterialGroup = (index: number) => {
    if (rawMaterialGroups.length === 1) return;
    setRawMaterialGroups((prev) => prev.filter((_, i) => i !== index));
    // Remove values for that group as well
    const groupNum = index + 1;
    const key = `rawMaterial_${String(groupNum).padStart(2, "0")}`;
    const qtyKey = `${key}_qty`;
    const unitKey = `rawMaterialUnit_${String(groupNum).padStart(2, "0")}`;
    setValues((prev) => {
      const next = { ...prev };
      delete next[key];
      delete next[qtyKey];
      delete next[unitKey];
      return next;
    });
  };

  // Load form, materials, units
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [formRes, materialsRes, unitsRes] = await Promise.all([
          axiosClient.get(`/forms/stage/${FORM_STAGE_CODES.BACKGROUND_DATA}`),
          axiosClient.get("/materials"),
          axiosClient.get("/units"),
        ]);

        // Form Data
        if (formRes.data.success && formRes.data.data) {
          const data = formRes.data.data as FormDataResponse;
          setFormData(data);

          const allFields = getBackgroundStageDisplayFields(data);

          const initValues: Record<string, string> = {};
          allFields.forEach((f) => {
            if (f.name === "a1_a3") {
              initValues[f.name] = resolveA1A3CheckboxDefault();
            } else if (f.type === "CHECKBOX" && ["a1", "a2", "a3"].includes(f.name)) {
              initValues[f.name] = "false";
            } else {
              initValues[f.name] = "";
            }
          });
          if (allFields.some((f) => f.name === "timelineAvailable")) {
            initValues.timelineAvailable =
              resolveTimelineAvailableDefault(allFields);
          }
          initValues[BACKGROUND_GEO_SPEC_FIELD] = "global";
          initValues[BACKGROUND_GEO_SELECTION_FIELD] = "global";
          setValues(initValues);
        }

        // Materials
        if (materialsRes.data.success && materialsRes.data.data) {
          const normalized = materialsRes.data.data.map((m: any) => ({
            id: m.id.toString(),
            name: m.name,
            code: m.code ?? "",
            subMaterials:
              m.subMaterials?.map((s: any) => ({
                id: s.id.toString(),
                name: s.name,
              })) || [],
          }));
          setMaterials(normalized);
        }

        // Units (flattened)
        if (unitsRes.data.success && Array.isArray(unitsRes.data.data)) {
          const flatUnits = unitsRes.data.data.flatMap((property: any) =>
            (property.units || []).map((u: any) => ({
              id: u.id,
              propertyId: property.id,
              unitSymbol: u.unitSymbol,
              displayName: u.displayName,
              isBase: u.isBase,
            }))
          );
          setAllUnits(flatUnits);
        }
      } catch (error) {
        console.error("❌ Error fetching Background Data:", error);
        toast.error("Failed to load background form data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill submission data
  useEffect(() => {
    const prefillSubmission = async () => {
      if (!submissionId || !formData || materials.length === 0) return;

      try {
        setLoading(true);
        await prefillFromSubmission({
          submissionId,
          formData,
          setValues,
          setEnabledPairs,
          setRawMaterialGroups,
          materials,
          setSubMaterials,
          setRawMaterials,
        });
      } catch (err) {
        console.error("❌ Failed to prefill background submission:", err);
      } finally {
        setLoading(false);
      }
    };

    prefillSubmission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, formData, materials]);

  const materialIdForSpecificities = String(values.materialId ?? "").trim();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!materialIdForSpecificities) {
        setTechnologyOptions([]);
        setManufacturerOptions([]);
        return;
      }

      const numId = Number(materialIdForSpecificities);
      if (!Number.isFinite(numId)) {
        setTechnologyOptions([]);
        setManufacturerOptions([]);
        return;
      }

      try {
        const [techRes, manRes] = await Promise.all([
          axiosClient.get(API_URLS.TECHNOLOGICAL_SPECIFICITIES.BY_MATERIAL(numId)),
          axiosClient.get(API_URLS.MANUFACTURER_SPECIFICITIES.BY_MATERIAL(numId)),
        ]);

        if (cancelled) return;

        const techList = extractListPayload(techRes)
          .map(mapTechnologyRow)
          .filter(Boolean) as SpecificityOption[];
        const manList = extractListPayload(manRes)
          .map(mapManufacturerRow)
          .filter(Boolean) as SpecificityOption[];

        setTechnologyOptions(techList);
        setManufacturerOptions(manList);

        setValues((prev) => {
          const tid = String(prev.technologyId ?? "").trim();
          const mid = String(prev.manufacturerId ?? "").trim();
          const techIds = new Set(techList.map((t) => t.id));
          const manIds = new Set(manList.map((m) => m.id));
          let changed = false;
          const next = { ...prev };
          if (techList.length > 0 && tid && !techIds.has(tid)) {
            next.technologyId = "";
            changed = true;
          }
          if (manList.length > 0 && mid && !manIds.has(mid)) {
            next.manufacturerId = "";
            changed = true;
          }
          return changed ? next : prev;
        });
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load material specificities:", e);
          setTechnologyOptions([]);
          setManufacturerOptions([]);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [materialIdForSpecificities]);

  const clearFieldErrors = (...names: string[]) => {
    setErrors((prev) => {
      const hasMatch = names.some((name) => prev[name]);
      if (!hasMatch) return prev;
      const next = { ...prev };
      names.forEach((name) => {
        delete next[name];
      });
      return next;
    });
  };

  // Handle material select
  const handleMaterialSelect = async (materialId: string) => {
    const selected = materials.find((m) => m.id === materialId);
    clearFieldErrors(
      "materialId",
      "subMaterialId",
      "technologyId",
      "manufacturerId",
      "rawMaterial_01"
    );
    setValues((prev) => ({
      ...prev,
      materialId,
      subMaterialId: "",
      materialCode: selected?.code || "",
      rawMaterial_01: "",
      technologyId: "",
      manufacturerId: "",
    }));
    setSubMaterials(selected?.subMaterials || []);

    // Fetch Raw Materials dynamically
    try {
      if (materialId) {
        const res = await axiosClient.get(
          `/raw-materials/material/${materialId}`
        );
        if (res.status === 200) {
          setRawMaterials(res.data);
        } else {
          setRawMaterials([]);
        }
      }
    } catch (error) {
      console.error("❌ Failed to fetch raw materials:", error);
      setRawMaterials([]);
    }
  };

  // Generic change handler
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    clearFieldErrors(name);
    if (/^rawMaterial_\d{2}$/.test(name)) {
      const qtyKey = `${name}_qty`;
      const unitKey = name.replace(/^rawMaterial_/, "rawMaterialUnit_");
      clearFieldErrors(qtyKey, unitKey);
    }
    if (name === "timelineAvailable") {
      clearFieldErrors(
        "collectionStartYear",
        "collectionStartMonth",
        "collectionStartDay",
        "collectionEndYear",
        "collectionEndMonth",
        "collectionEndDay"
      );
    }
    if (name === "dataReview") {
      for (let i = 1; i <= 3; i++) {
        clearFieldErrors(dataReviewerNameKey(i), dataReviewerEmailKey(i));
      }
    }
    setValues((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "dataReview") {
        const slug = normalizeDataReviewValue(value, dataReviewFieldOptions);
        next.dataReview = slug || value;
        const count = requiredDataReviewerCount(slug || value, dataReviewFieldOptions);
        for (let i = count + 1; i <= 3; i++) {
          delete next[dataReviewerNameKey(i)];
          delete next[dataReviewerEmailKey(i)];
        }
      }

      if (name === "dataType") {
        next.dataType =
          normalizeDataTypeValue(value, dataTypeFieldOptionsList) || value;
      }

      // Enable property pairs ONLY when explicitly Yes.
      // If No or blank/other → force-disable all property pairs.
      if (name === "materialPropertiesAvailable" && !isYes(value)) {
        Object.keys(pairedGroups).forEach((mainKey) => {
          next[mainKey] = "";
          next[pairedGroups[mainKey]] = "";
        });
      }

      return next;
    });

    if (name === "materialPropertiesAvailable" && !isYes(value)) {
      setEnabledPairs((prev) => {
        const next = { ...prev };
        Object.keys(pairedGroups).forEach((k) => {
          next[k] = false;
        });
        return next;
      });
    }
  };

  // File change handler
  const handleFileChange = (fieldName: string, file: File) => {
    clearFieldErrors(fieldName);
    const previewUrl = URL.createObjectURL(file);
    setValues((prev) => ({
      ...prev,
      [fieldName]: file,
      [`${fieldName}_preview`]: previewUrl,
    }));
  };

  // Checkbox change handler
  const handleCheckboxChange = (fieldName: string, checked: boolean) => {
    clearFieldErrors(fieldName, "a1", "a2", "a3", "a1_a3", "dataCompleteness");
    setValues((prev) => {
      const next = {
        ...prev,
        [fieldName]: checked ? "true" : "false",
      };
      if (["a1", "a2", "a3"].includes(fieldName)) {
        const a1 = next["a1"] === "true";
        const a2 = next["a2"] === "true";
        const a3 = next["a3"] === "true";
        if (a1 && a2 && a3) {
          next["a1_a3"] = "true";
        }
      }
      return next;
    });
  };

  /** When A1, A2, and A3 are all selected (e.g. after prefill), ensure A1-A3 is checked too. */
  const a1Flag = values["a1"];
  const a2Flag = values["a2"];
  const a3Flag = values["a3"];
  useEffect(() => {
    setValues((prev) => {
      const a1 = prev["a1"] === "true";
      const a2 = prev["a2"] === "true";
      const a3 = prev["a3"] === "true";
      if (a1 && a2 && a3 && prev["a1_a3"] !== "true") {
        return { ...prev, a1_a3: "true" };
      }
      return prev;
    });
  }, [a1Flag, a2Flag, a3Flag]);

  const handleGeoSpecChange = (next: BackgroundGeoSpec) => {
    clearFieldErrors(BACKGROUND_GEO_SELECTION_FIELD);
    setValues((prev) => ({
      ...prev,
      [BACKGROUND_GEO_SPEC_FIELD]: next,
      [BACKGROUND_GEO_SELECTION_FIELD]:
        next === "global" ? "global" : next === "india" ? "India" : "",
    }));
  };

  const handleGeoSelectionChange = (value: string) => {
    clearFieldErrors(BACKGROUND_GEO_SELECTION_FIELD);
    setValues((prev) => ({ ...prev, [BACKGROUND_GEO_SELECTION_FIELD]: value }));
  };

  // Build payload and call save API
  const handleSubmit = async (): Promise<boolean> => {
    try {
      if (!submissionId) {
        toast.error("Submission ID missing. Save failed.");
        return false;
      }

      if (!formData) {
        toast.error("Form data is not available.");
        return false;
      }

      const formErrors = validateBackgroundForm({
        formData,
        values,
        enabledPairs,
        rawMaterialGroups,
        reviewerGroups,
        normalizedDataReview,
        dataReviewFieldOptions,
      });
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        toast.error(
          getFirstBackgroundFormError(formErrors) ??
            "Please fix the highlighted fields."
        );
        return false;
      }
      setErrors({});

      const revokeBlobPreviews = (src: Record<string, string | File>) => {
        Object.entries(src).forEach(([k, v]) => {
          if (k.endsWith("_preview") && typeof v === "string" && v.startsWith("blob:")) {
            URL.revokeObjectURL(v);
          }
        });
      };

      let mergedValues: Record<string, string | File> = { ...values };

      const fd = new FormData();
      if (values.materialImage instanceof File) {
        fd.append("materialImage", values.materialImage);
      }
      if (values.publicationFile instanceof File) {
        fd.append("publicationFile", values.publicationFile);
      }

      const hasBackgroundUpload =
        values.materialImage instanceof File ||
        values.publicationFile instanceof File;

      setLoading(true);

      if (hasBackgroundUpload) {
        const uploadRes = await uploadBackgroundFiles(submissionId, fd);
        if (!uploadRes.data?.success) {
          toast.error(uploadRes.data?.message || "File upload failed");
          return false;
        }
        const urls = uploadRes.data?.data as
          | { materialImageUrl?: string; publicationFileUrl?: string }
          | undefined;
        mergedValues = { ...mergedValues };
        if (urls?.materialImageUrl) {
          mergedValues.materialImageUrl = urls.materialImageUrl;
          mergedValues.materialImage = urls.materialImageUrl;
          delete mergedValues.materialImage_preview;
        }
        if (urls?.publicationFileUrl) {
          mergedValues.publicationFileUrl = urls.publicationFileUrl;
          mergedValues.publicationFile = urls.publicationFileUrl;
          delete mergedValues.publicationFile_preview;
        }
      }

      const payload = buildBackgroundPayload(
        mergedValues,
        rawMaterialGroups,
        reviewerGroups,
        dataTypeFieldOptionsList
      );

      const res = await saveBackgroundData(submissionId, payload);
      if (res.data?.success) {
        revokeBlobPreviews(values);
        setValues((prev) => {
          const next = { ...prev, ...mergedValues };
          if (mergedValues.materialImageUrl) {
            next.materialImage = String(mergedValues.materialImageUrl);
          }
          if (mergedValues.publicationFileUrl) {
            next.publicationFile = String(mergedValues.publicationFileUrl);
          }
          return next;
        });
        toast.success("Background data saved!", {
          style: {
            backgroundColor: "#10b981",
            color: "#ffffff",
            fontSize: "16px",
            fontWeight: "500",
          },
        });
        return true;
      } else {
        toast.error(res.data?.message || "Failed to save background data");
        return false;
      }
    } catch (err: any) {
      console.error("❌ Error saving background data:", err);
      toast.error(err?.response?.data?.message || "Save failed");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    values,
    setValues,
    loading,
    materials,
    subMaterials,
    rawMaterials,
    technologyOptions,
    manufacturerOptions,
    allUnits,
    enabledPairs,
    setEnabledPairs,
    rawMaterialGroups,
    reviewerGroups,
    normalizedDataReview,
    externalDataReviewSelected,
    dataReviewFieldOptions,
    pairedGroups,
    addRawMaterialGroup,
    removeRawMaterialGroup,
    handleMaterialSelect,
    handleChange,
    handleFileChange,
    handleCheckboxChange,
    handleGeoSpecChange,
    handleGeoSelectionChange,
    stateOptions,
    geographicScopeOptions,
    geographicScopeLoading,
    handleSubmit,
    errors,
  };
}

